import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { countryNameFor } from '../../util/custom/countryNames';
import { isSellerGroupLocked } from '../../util/custom/cart';
import { PENDING } from '../../util/custom/cartAvailability';
import CartItemRow from '../CartItemRow/CartItemRow';
import CartOrderSummary from '../CartOrderSummary/CartOrderSummary';
import IconAlert from '../IconAlert/IconAlert';
import IconSpinner from '../IconSpinner/IconSpinner';
import { PrimaryButton } from '../Button/Button';
import NamedLink from '../NamedLink/NamedLink';

import css from './CartSellerGroup.module.css';

/**
 * CL-CART-01 — one seller's panel: who is selling, the rows, the subtotal, the button
 * (AC-10, requirement 9).
 *
 * The summary and the checkout button live HERE, per seller, because section M puts
 * mixed-seller checkout in one payment out of scope: one payment covers one seller. A
 * cart-wide total with a single button was a figure nobody could pay and a button that
 * could not say what it was buying.
 *
 * This component also owns the one decision a single row cannot make: whether the panel has
 * a quantity COLUMN. A column is shared by every row under it, so it exists when any row
 * needs it — and a row that cannot move states its quantity instead (see CartItemRow).
 */
const CartSellerGroup = props => {
  const {
    className,
    rootClassName,
    group,
    fetchInProgress,
    onSetQuantity,
    onRemoveItem,
    onCheckout,
  } = props;
  const intl = useIntl();

  const {
    authorId,
    authorDisplayName,
    items = [],
    itemSubtotalInSubunits,
    unavailableItemCount = 0,
    blocksCheckout,
  } = group;
  const currency = items.find(i => i.currency)?.currency;

  // CL-NEG-01 requirement 13 — an open offer freezes this ONE seller's group. NOT because the offer
  // holds that stock: under D12 it holds nothing. Because the offer is PRICED from a frozen
  // `protectedData.cart` snapshot (offer-initiate.js writes it, runPaymentJoin re-reads it), so a
  // basket edited underneath a live negotiation would make this page and the offer describe two
  // different orders — and "Change items" would silently mean "make a different offer".
  // `lockedByTransactionId` rides straight through from the raw cart group (annotateSellerGroup
  // spreads it), so this is the one place the lock turns into rendering decisions.
  const isLocked = isSellerGroupLocked(group);
  // Every row forced read-only together: no quantity control (requirement 14) and no remove
  // button (requirement 13), whatever an individual row's own annotation would otherwise allow.
  const displayItems = isLocked
    ? items.map(item => ({ ...item, showsQuantityControl: false }))
    : items;

  const showsQuantityColumn = displayItems.some(i => i.showsQuantityControl);

  // D6's gate, computed from the annotated rows the panel already has (`status`, `title` and
  // `offersEnabled` all come from annotateCartItem). A row still loading (PENDING) is NOT
  // counted as Buy It Now only — the panel would otherwise flash the blocked message on every
  // first paint, which reads as a rule that later mysteriously stops applying.
  const binOnlyTitles = items
    .filter(i => i.status !== PENDING && i.offersEnabled !== true)
    .map(i => i.title);

  // Where the seller ships from, stated only when it is true of the WHOLE panel. Origin is
  // a property of the listing, not of the seller: CL-STOCK-01 lets one seller hold stock in
  // several locations, so two rows under one name can ship from two countries. Naming one
  // of them in the panel header would be a plain falsehood about the other, so a mixed
  // panel says nothing here and leaves each row to its own shipping line.
  //
  // A deleted listing has no origin at all (annotateCartItem's fallback shipping object),
  // which the filter drops rather than counting as a second country.
  const origins = [...new Set(items.map(i => i.shipping?.originCountry).filter(Boolean))];
  // The CODE is passed down so a row can tell whether the header already covered it; the NAME
  // is only for display. SL2: always store and compare the code, only ever show the name.
  const panelOriginCountry = origins.length === 1 ? origins[0] : null;
  const originCountry = panelOriginCountry ? countryNameFor(panelOriginCountry, intl.locale) : null;

  return (
    <section className={classNames(rootClassName || css.root, className)}>
      <header className={css.panelHeader}>
        <div>
          <span className={css.eyebrow}>
            <FormattedMessage id="CartSellerGroup.soldBy" />
          </span>
          <h2 className={css.sellerName}>{authorDisplayName}</h2>
        </div>

        <div className={css.headerMeta}>
          {/* Rows paint from the cart's stored snapshots and then reconcile against the live
              listings, so a price or an availability can change under the buyer with no
              explanation. Saying so is the difference between a figure that moved and a
              figure that looks wrong. */}
          {fetchInProgress ? (
            <span className={css.checking}>
              <IconSpinner className={css.checkingSpinner} />
              <FormattedMessage id="CartSellerGroup.checkingAvailability" />
            </span>
          ) : null}
          {/* A pill only ever means a problem, so the shape itself carries the signal. */}
          {unavailableItemCount > 0 ? (
            <span className={css.blockedPill}>
              <IconAlert className={css.blockedPillIcon} />
              <FormattedMessage
                id="CartSellerGroup.unavailableCount"
                values={{ count: unavailableItemCount }}
              />
            </span>
          ) : null}
          <span>
            {/* The separator lives inside the translation, so a translator can reorder or
                replace it rather than being stuck with a hardcoded interpunct. */}
            {originCountry ? (
              <FormattedMessage
                id="CartSellerGroup.itemCountFrom"
                values={{ count: items.length, country: originCountry }}
              />
            ) : (
              <FormattedMessage id="CartSellerGroup.itemCount" values={{ count: items.length }} />
            )}
          </span>
        </div>
      </header>

      {/* CL-NEG-01 requirement 13: an open offer freezes this group. A link back to the
          negotiation, not a summary of it — the offer page already tells that story. */}
      {isLocked ? (
        <p className={css.lockedBanner}>
          <IconAlert className={css.lockedBannerIcon} />
          <FormattedMessage id="CartPage.lockedBanner" />{' '}
          <NamedLink
            name="OrderDetailsPage"
            params={{ id: group.lockedByTransactionId }}
            className={css.lockedBannerLink}
          >
            <FormattedMessage id="CartPage.lockedBannerLink" />
          </NamedLink>
        </p>
      ) : null}

      <div className={css.rows}>
        {/* data-testid: the cell COUNT here has to match the rows, or grid auto-placement
            shifts every label off the column it names. Asserted in CartPage.test.js, where
            CSS is stubbed and only the DOM is observable. */}
        <div
          data-testid="cart-column-headers"
          className={classNames(css.columnHeaders, {
            [css.columnHeadersNarrow]: !showsQuantityColumn,
          })}
        >
          <span />
          <span>
            <FormattedMessage id="CartSellerGroup.columnItem" />
          </span>
          {showsQuantityColumn ? (
            <span className={css.columnQuantity}>
              <FormattedMessage id="CartSellerGroup.columnQuantity" />
            </span>
          ) : null}
          <span className={css.columnMoney}>
            <FormattedMessage
              id={
                showsQuantityColumn
                  ? 'CartSellerGroup.columnLineTotal'
                  : 'CartSellerGroup.columnPrice'
              }
            />
          </span>
          <span />
        </div>

        <ul className={css.items}>
          {displayItems.map(item => (
            <CartItemRow
              key={item.listingId}
              item={item}
              showsQuantityColumn={showsQuantityColumn}
              panelOriginCountry={panelOriginCountry}
              hideRemove={isLocked}
              onSetQuantity={({ listingId, quantity }) =>
                onSetQuantity({ authorId, listingId, quantity })
              }
              onRemove={({ listingId }) => onRemoveItem({ authorId, listingId })}
            />
          ))}
        </ul>
      </div>

      <footer className={css.panelFooter}>
        <p
          className={classNames(css.footerNote, {
            [css.footerNoteBlocked]: unavailableItemCount > 0,
          })}
        >
          <IconAlert className={css.footerNoteIcon} />
          {unavailableItemCount > 0 ? (
            <FormattedMessage
              id="CartSellerGroup.blockedNote"
              values={{ count: unavailableItemCount }}
            />
          ) : (
            <FormattedMessage id="CartSellerGroup.notReserved" />
          )}
        </p>

        <div className={css.summaryBlock}>
          {/* Only what can actually be bought (D-12). A subtotal that included a sold-out
              row would be a number the buyer can never pay. */}
          <CartOrderSummary
            className={css.summary}
            itemSubtotalInSubunits={itemSubtotalInSubunits}
            currency={currency}
            unavailableItemCount={unavailableItemCount}
          />

          {isLocked ? (
            // Deliberately a link and not a withdraw button: a destructive transition belongs
            // in one place, beside the history that explains what is being given up — the
            // offer page (Task 11 §A), not this panel.
            <NamedLink name="CartOfferPage" params={{ authorId }} className={css.changeItems}>
              <FormattedMessage id="CartPage.lockedChangeItems" />
            </NamedLink>
          ) : (
            <>
              {/* Disabled rather than hidden: the reason is stated on the row above it and
                  again beside this button, and a missing button reads as a broken page
                  (D-12). The seller's name is in the label because with more than one panel
                  "Go to checkout" cannot say what it buys. */}
              <PrimaryButton
                className={css.checkout}
                type="button"
                disabled={blocksCheckout || fetchInProgress}
                onClick={() => onCheckout({ authorId })}
              >
                <FormattedMessage
                  id="CartSellerGroup.checkout"
                  values={{ seller: authorDisplayName }}
                />
              </PrimaryButton>

              {/* D6: every item's listing must allow offers, or the action is hidden with the
                  Buy It Now only item NAMED — a hidden control with no explanation reads as a
                  bug. */}
              {binOnlyTitles.length === 0 ? (
                <NamedLink name="CartOfferPage" params={{ authorId }} className={css.bundleOffer}>
                  <FormattedMessage id="CartSellerGroup.makeBundleOffer" />
                </NamedLink>
              ) : (
                <p className={css.bundleOfferBlocked}>
                  <FormattedMessage
                    id="CartSellerGroup.bundleOfferBlocked"
                    values={{ count: binOnlyTitles.length, titles: binOnlyTitles.join(', ') }}
                  />
                </p>
              )}
            </>
          )}
        </div>
      </footer>
    </section>
  );
};

export default CartSellerGroup;
